import { useMemo, useRef, useState, type FC, type PointerEvent } from "react";
import type { PlaneCoordinate, PlanePoint, ToScreenPoint } from "./CartesianPlane";
import { getSlopeArrowCoordinates, toSlopeArrow, type SlopeArrow } from "./deckOutline";
import SlopeArrowLayer from "./SlopeArrowLayer";
import styles from "./SlopeArrowDrawingLayer.module.css";

// A shorter drag is treated as a click, not as an arrow.
const MIN_ARROW_LENGTH_PX = 8;

type ArrowDraft = {
  pointerId: number;
  start: PlanePoint;
  end: PlanePoint;
};

type SlopeArrowDrawingLayerProps = {
  // The deck's outline the arrow is saved relative to (see toSlopeArrow).
  coordinates: PlaneCoordinate[];
  onSlopeArrowChange: (slopeArrow: SlopeArrow) => void;
  slopeArrow: SlopeArrow | null;
  toPlanePointFromClient: (clientX: number, clientY: number) => PlanePoint | null;
  toScreenPoint: ToScreenPoint;
};

// The slope arrow over SlopeDirectionModal's plane: the saved one, or the one being drawn by dragging
// anywhere over the plane. Owns the drag's draft, so only this layer re-renders while drawing - not
// the modal, nor the plane's other layers.
const SlopeArrowDrawingLayer: FC<SlopeArrowDrawingLayerProps> = ({
  coordinates,
  onSlopeArrowChange,
  slopeArrow,
  toPlanePointFromClient,
  toScreenPoint,
}) => {
  const [arrowDraft, setArrowDraft] = useState<ArrowDraft | null>(null);
  const arrowDraftRef = useRef<ArrowDraft | null>(null);
  const savedArrow = useMemo(
    () => (slopeArrow ? getSlopeArrowCoordinates(coordinates, slopeArrow) : null),
    [coordinates, slopeArrow],
  );
  const arrow: { start: PlaneCoordinate; end: PlaneCoordinate } | null = arrowDraft ?? savedArrow;

  const updateArrowDraft = (nextDraft: ArrowDraft | null) => {
    arrowDraftRef.current = nextDraft;
    setArrowDraft(nextDraft);
  };

  const startArrow = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    const start = toPlanePointFromClient(event.clientX, event.clientY);
    if (!start) {
      return;
    }

    // Keeps the plane from panning: dragging here draws the arrow instead.
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    updateArrowDraft({ pointerId: event.pointerId, start, end: start });
  };

  const moveArrow = (event: PointerEvent<HTMLDivElement>) => {
    const draft = arrowDraftRef.current;
    const end = draft?.pointerId === event.pointerId ? toPlanePointFromClient(event.clientX, event.clientY) : null;

    if (draft && end) {
      updateArrowDraft({ ...draft, end });
    }
  };

  const finishArrow = (event: PointerEvent<HTMLDivElement>) => {
    const draft = arrowDraftRef.current;

    if (!draft || draft.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    updateArrowDraft(null);

    // Measured on screen, not in meters, so the threshold doesn't depend on the zoom.
    if (
      Math.hypot(draft.end.pixelX - draft.start.pixelX, draft.end.pixelY - draft.start.pixelY) < MIN_ARROW_LENGTH_PX
    ) {
      return;
    }

    // The points come from the unrounded pixels, not from the plane coordinates snapped to the cm,
    // so even a short arrow keeps the direction it was drawn with.
    const origin = toScreenPoint({ x: 0, y: 0 });
    const unit = toScreenPoint({ x: 1, y: 0 });
    if (!origin || !unit) {
      return;
    }
    const pixelsPerMeter = unit.pixelX - origin.pixelX;
    const toCoordinate = (point: PlanePoint): PlaneCoordinate => ({
      x: (point.pixelX - origin.pixelX) / pixelsPerMeter,
      y: (origin.pixelY - point.pixelY) / pixelsPerMeter,
    });
    const nextArrow = toSlopeArrow(coordinates, toCoordinate(draft.start), toCoordinate(draft.end));

    // Zoomed far enough in, a few pixels can still round to the same millimeter.
    if (nextArrow.startXMm !== nextArrow.endXMm || nextArrow.startYMm !== nextArrow.endYMm) {
      onSlopeArrowChange(nextArrow);
    }
  };

  return (
    <>
      {arrow && <SlopeArrowLayer start={arrow.start} end={arrow.end} toScreenPoint={toScreenPoint} />}
      <div
        className={styles.arrowCapture}
        onPointerDown={startArrow}
        onPointerMove={moveArrow}
        onPointerUp={finishArrow}
        onPointerCancel={finishArrow}
      />
    </>
  );
};

export default SlopeArrowDrawingLayer;
